import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Search, CreditCard, Eye } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { PageHeader } from '@/components/page-header';
import { usePayments } from '@/hooks/use-payments';
import type { ApiPaymentMethod } from '@/types';
import { clampPage, resolveTotalItems, resolveTotalPages } from '@/lib/pagination';

export const Route = createFileRoute('/payments/')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <PaymentsPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
}

function safeFormatDate(dateString: string) {
  try {
    return format(new Date(dateString), 'dd MMM yyyy, HH:mm');
  } catch {
    return '-';
  }
}

const methodLabels: Record<ApiPaymentMethod, string> = {
  CASH: 'Cash',
  GOPAY: 'GoPay',
  BANK_TRANSFER: 'Bank Transfer',
};

function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [page, setPage] = useState(1);

  const { data: paymentsResponse, isLoading, error } = usePayments({
    search: search || undefined,
    paymentMethod: methodFilter === 'all' ? undefined : methodFilter,
    page,
  });

  const payments = paymentsResponse?.data || [];
  const pagination = paymentsResponse?.pagination;
  const totalItems = resolveTotalItems(pagination, payments.length);
  const totalPages = resolveTotalPages(pagination, payments.length);

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader title="Payments" />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative w-full flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by payment number or order..."
            className="pl-9"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Select
          value={methodFilter}
          onValueChange={(value) => {
            setMethodFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Payment method..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="CASH">Cash</SelectItem>
            <SelectItem value="GOPAY">GoPay</SelectItem>
            <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Payments</CardTitle>
          <CardDescription>
            View and track all payments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <p>Failed to load payments. Please try again.</p>
            </div>
          ) : !payments.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCard className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No payments yet</p>
              <p className="text-sm">Payments will appear here once processed.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-3 md:hidden">
                {payments.map((payment) => (
                  <article key={payment.id} className="rounded-xl border bg-card p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">{payment.paymentNumber}</p>
                        <p className="text-xs text-muted-foreground">{safeFormatDate(payment.paymentDate)}</p>
                      </div>
                      <Badge variant="secondary">
                        {methodLabels[payment.paymentMethod] || payment.paymentMethod}
                      </Badge>
                    </div>

                    <Link
                      to="/orders/$id"
                      params={{ id: String(payment.orderId) }}
                      className="mt-2 block text-sm font-medium text-primary hover:underline"
                    >
                      {payment.order.orderNumber}
                    </Link>

                    <div className="mt-3 rounded-md bg-muted/40 px-3 py-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Amount</p>
                      <p className="text-sm font-semibold">{formatCurrency(payment.amount)}</p>
                    </div>

                    <Link to="/orders/$id" params={{ id: String(payment.orderId) }}>
                      <Button variant="outline" className="mt-3 w-full gap-2">
                        <Eye className="h-4 w-4" />
                        View Order
                      </Button>
                    </Link>
                  </article>
                ))}
              </div>

              <div className="hidden overflow-x-auto rounded-md border md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payment #</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium">{payment.paymentNumber}</TableCell>
                        <TableCell>
                          <Link
                            to="/orders/$id"
                            params={{ id: String(payment.orderId) }}
                            className="text-primary hover:underline"
                          >
                            {payment.order.orderNumber}
                          </Link>
                        </TableCell>
                        <TableCell>{safeFormatDate(payment.paymentDate)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {methodLabels[payment.paymentMethod] || payment.paymentMethod}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to="/orders/$id" params={{ id: String(payment.orderId) }}>
                            <Button variant="ghost" size="icon">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {pagination && (
            <div className="mt-4">
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
                totalItems={totalItems}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
